import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, StickyNote } from 'lucide-react'
import type { ReactNode } from 'react'
import type { ProjectInfo } from './useSearch'
import type { SearchItem } from './rankSearch'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { useActiveCycle } from '@/lib/db/rxdb/hooks/entities/cycles'
import { useCycleWorkItemsByCycle } from '@/lib/db/rxdb/hooks/entities/cycleWorkItems'
import { useTaskContent } from '@/lib/db/rxdb/hooks/entities/task-content'
import { usePageContent } from '@/lib/db/rxdb/hooks/entities/page-content'
import { usePersonalNoteContent } from '@/lib/db/rxdb/hooks/entities/personal-note-content'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { resolveTaskIdentifier } from '@/routes/features/tasks/utils/taskIdentifier'
import { getPlatformShortcutKeys } from '@/lib/utils/keyboard'

const DONE = new Set(['CD', 'CL'])
const SNIPPET_LENGTH = 280

interface SearchPreviewProps {
  item: SearchItem
  tasks: ReadonlyArray<TaskDocument> | undefined
  projectMap: ReadonlyMap<string, ProjectInfo>
  statusMap: ReadonlyMap<string, StatusDocument>
}

/**
 * The right-hand pane of ⌘K (design 29b): what the selected result is, a
 * little of what's inside, and what ↵, ⌘C and → will do.
 */
export function SearchPreview({ item, tasks, projectMap, statusMap }: SearchPreviewProps) {
  const { t } = useTranslation('common')
  const project = item.type === 'project' ? projectMap.get(item.id) : undefined

  return (
    <div
      data-testid="search-preview"
      className="flex min-h-0 w-[336px] shrink-0 flex-col border-l border-border bg-chrome px-[18px] py-4"
    >
      <div className="min-h-0 flex-1 overflow-y-auto">
        {item.type === 'project' && project && (
          <ProjectPreview project={project} item={item} tasks={tasks} projectMap={projectMap} statusMap={statusMap} />
        )}
        {item.type === 'task' && <TaskPreview item={item} tasks={tasks} statusMap={statusMap} />}
        {item.type === 'page' && <PagePreview item={item} />}
        {item.type === 'note' && <NotePreview item={item} />}
      </div>
      <div className="mt-3 flex flex-col gap-1.5 text-[12.5px] text-secondary-foreground">
        <PreviewAction keys="↵" label={t('search.actions.open')} />
        <PreviewAction keys={getPlatformShortcutKeys('C').join('')} label={t('search.actions.copyLink')} />
        {item.type === 'project' && (
          <PreviewAction keys="→" label={t('search.actions.searchInside', { name: item.name })} />
        )}
      </div>
    </div>
  )
}

function PreviewAction({ keys, label }: { keys: string; label: string }) {
  return (
    <span className="flex items-center gap-2">
      <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[4px] border border-b-2 border-input bg-card px-1 font-mono text-[10px] text-muted-foreground">
        {keys}
      </kbd>
      <span className="truncate">{label}</span>
    </span>
  )
}

function PreviewHeader({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <div className="break-words text-[15px] font-semibold leading-snug text-foreground">{title}</div>
        {subtitle && <div className="truncate text-[12.5px] text-muted-foreground">{subtitle}</div>}
      </div>
    </div>
  )
}

function Snippet({ text }: { text: string | null | undefined }) {
  const { t } = useTranslation('common')
  const trimmed = (text ?? '').trim()
  return (
    <p className="mt-4 line-clamp-[8] whitespace-pre-line text-[12.5px] leading-relaxed text-secondary-foreground">
      {trimmed ? trimmed.slice(0, SNIPPET_LENGTH) : (
        <span className="text-muted-foreground">{t('search.preview.empty')}</span>
      )}
    </p>
  )
}

function CycleRing({ percent }: { percent: number }) {
  const radius = 11
  const circumference = 2 * Math.PI * radius
  return (
    <svg viewBox="0 0 28 28" className="size-7 shrink-0 -rotate-90" aria-hidden>
      <circle cx="14" cy="14" r={radius} fill="none" strokeWidth="3.5" className="stroke-border" />
      <circle
        cx="14"
        cy="14"
        r={radius}
        fill="none"
        strokeWidth="3.5"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - percent / 100)}
        className="stroke-brand"
      />
    </svg>
  )
}

function ProjectPreview({
  project,
  item,
  tasks,
  projectMap,
  statusMap,
}: {
  project: ProjectInfo
  item: SearchItem
  tasks: ReadonlyArray<TaskDocument> | undefined
  projectMap: ReadonlyMap<string, ProjectInfo>
  statusMap: ReadonlyMap<string, StatusDocument>
}) {
  const { t } = useTranslation('common')
  const formatters = useDateTimeFormatters()
  const { activeCycle } = useActiveCycle(project.isCyclesEnabled ? project.id : undefined)
  const { cycleWorkItems } = useCycleWorkItemsByCycle(activeCycle?.id)

  const taskById = useMemo(() => new Map((tasks ?? []).map((task) => [task.id, task])), [tasks])
  const progressOf = useCallback(
    (task: TaskDocument | undefined) =>
      task?.statusId ? statusMap.get(task.statusId)?.progressType : undefined,
    [statusMap],
  )

  const cyclePercent = useMemo(() => {
    const live = (cycleWorkItems ?? []).filter((row) => !row.isDeleted)
    if (live.length === 0) return 0
    const done = live.filter((row) => DONE.has(progressOf(taskById.get(row.workItemId)) ?? '')).length
    return Math.round((done / live.length) * 100)
  }, [cycleWorkItems, taskById, progressOf])

  const openTasks = useMemo(
    () =>
      (tasks ?? [])
        .filter(
          (task) =>
            task.projectId === project.id &&
            !task.isDeleted &&
            !task.isArchived &&
            !task.parentId &&
            !DONE.has(progressOf(task) ?? ''),
        )
        .sort((a, b) => b.updatedOn - a.updatedOn),
    [tasks, project.id, progressOf],
  )

  const space = project.parentProjectId ? projectMap.get(project.parentProjectId) : undefined

  return (
    <>
      <PreviewHeader
        icon={
          <ProjectIcon
            variant="badge"
            iconType={item.iconType}
            icon={item.icon}
            iconColor={item.iconColor}
            fallbackLabel={item.name}
            size={24}
            className="rounded-[7px]"
          />
        }
        title={item.name}
        subtitle={space ? t('search.preview.projectIn', { space: space.name }) : t('search.preview.space')}
      />
      {activeCycle && (
        <div className="mt-4 flex items-center gap-2.5">
          <CycleRing percent={cyclePercent} />
          <div className="min-w-0 text-[12.5px] leading-tight">
            <div className="font-medium text-foreground">
              {t('search.preview.cycle', { number: activeCycle.number, percent: cyclePercent })}
            </div>
            <div className="text-muted-foreground">
              {t('search.preview.ends', {
                date: formatters.date(new Date(activeCycle.endDate), { month: 'short', day: 'numeric' }),
              })}
            </div>
          </div>
        </div>
      )}
      <div className="mt-4 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {t('search.preview.open')}
        {openTasks.length > 0 && <span className="ml-1 font-mono">{openTasks.length}</span>}
      </div>
      {openTasks.length === 0 ? (
        <p className="mt-1.5 text-[12.5px] text-muted-foreground">{t('search.preview.nothingOpen')}</p>
      ) : (
        <ul className="mt-1.5 flex flex-col gap-1">
          {openTasks.slice(0, 6).map((task) => (
            <li key={task.id} className="flex min-w-0 items-center gap-2 text-[12.5px] text-foreground">
              <StatusIcon progressType={progressOf(task) ?? 'BL'} statusId={task.statusId} />
              <span className="shrink-0 font-mono text-[10.5px] text-muted-foreground">
                {resolveTaskIdentifier(task, project.projectKey)}
              </span>
              <span className="truncate">{task.name}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function TaskPreview({
  item,
  tasks,
  statusMap,
}: {
  item: SearchItem
  tasks: ReadonlyArray<TaskDocument> | undefined
  statusMap: ReadonlyMap<string, StatusDocument>
}) {
  const { t } = useTranslation(['common', 'tasks'])
  const formatters = useDateTimeFormatters()
  const { content } = useTaskContent(item.id)
  const task = useMemo(() => tasks?.find((candidate) => candidate.id === item.id), [tasks, item.id])
  const status = item.statusId ? statusMap.get(item.statusId) : undefined
  const priorityLabels: Record<number, string> = {
    1: t('tasks:priority.low'),
    2: t('tasks:priority.medium'),
    3: t('tasks:priority.high'),
    4: t('tasks:priority.urgent'),
  }

  const rows: Array<[string, string]> = [
    [t('globalCreate.chips.status'), status?.name ?? '—'],
    [t('globalCreate.chips.priority'), priorityLabels[task?.priority ?? 0] ?? '—'],
    [
      t('globalCreate.chips.assignee'),
      task?.userName || task?.userEmail || t('search.preview.unassigned'),
    ],
    [
      t('globalCreate.chips.due'),
      task?.dueDate
        ? formatters.date(new Date(task.dueDate), { month: 'short', day: 'numeric' })
        : t('search.preview.noDue'),
    ],
  ]

  return (
    <>
      <PreviewHeader
        icon={<StatusIcon progressType={item.progressType ?? 'BL'} statusId={item.statusId ?? undefined} />}
        title={item.name}
        subtitle={[item.identifier, item.projectName ?? t('search.preview.inbox')].filter(Boolean).join(' · ')}
      />
      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12.5px]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="truncate text-foreground">{value}</dd>
          </div>
        ))}
      </dl>
      <Snippet text={content?.plainText} />
    </>
  )
}

function PagePreview({ item }: { item: SearchItem }) {
  const { content } = usePageContent(item.id)
  return (
    <>
      <PreviewHeader
        icon={<FileText className="size-5 text-muted-foreground" />}
        title={item.name || '—'}
        subtitle={[item.spaceName, item.projectName !== item.spaceName ? item.projectName : null]
          .filter(Boolean)
          .join(' / ')}
      />
      <Snippet text={content?.plainText} />
    </>
  )
}

function NotePreview({ item }: { item: SearchItem }) {
  const { t } = useTranslation('common')
  const { content } = usePersonalNoteContent(item.id)
  return (
    <>
      <PreviewHeader
        icon={<StickyNote className="size-5 text-muted-foreground" />}
        title={item.name}
        subtitle={
          item.noteType === 'weekly' ? t('search.preview.weeklyNote') : t('search.preview.dailyNote')
        }
      />
      <Snippet text={content?.plainText} />
    </>
  )
}
